import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence, useInView } from 'framer-motion'
import {
  AlertTriangle,
  ChevronDown,
  Users,
  Eye,
  Brain,
  ArrowRight,
  Zap,
  BarChart3,
  Building2,
  Activity,
  Target,
  Info,
  CheckCircle,
  Mic,
  MicOff,
  Video,
  MessageSquare,
  PhoneOff,
  MoreHorizontal,
  Play,
} from 'lucide-react'
import Navbar from '../components/layout/Navbar'
import Footer from '../components/layout/Footer'
import { usePageMeta } from '../hooks/usePageMeta'

// ─── Sparkline ────────────────────────────────────────────────────────────────

function SparkLine({ data, down = false }) {
  const w = 72, h = 28
  const max = Math.max(...data), min = Math.min(...data)
  const range = max - min || 1
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w
    const y = h - ((v - min) / range) * (h - 6) - 3
    return `${x},${y}`
  }).join(' ')
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={down ? '#ef4444' : '#16c1b2'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ─── Hero Visualization — The Ghost Meeting ───────────────────────────────────

function HeroVisualization() {
  const [phase, setPhase] = useState(0)
  const [speaker, setSpeaker] = useState(0)

  // Cycle active speaker to simulate a live meeting
  useEffect(() => {
    const seq = [0, 2, 4, 1, 3, 0, 2]
    let i = 0
    const id = setInterval(() => { i = (i + 1) % seq.length; setSpeaker(seq[i]) }, 2420)
    return () => clearInterval(id)
  }, [])

  // Phase loop — all waits +10% slower
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      while (!cancelled) {
        setPhase(0)
        await wait(4400)
        if (cancelled) break
        setPhase(1)
        await wait(3080)
        if (cancelled) break
        setPhase(2)
        await wait(4620)
      }
    }
    run()
    return () => { cancelled = true }
  }, [])

  const tiles = [
    { name: 'Sarah K.',  initials: 'SK', color: '#2563eb' },
    { name: 'Marcus L.', initials: 'ML', color: '#7c3aed' },
    { name: 'Jamie R.',  initials: 'JR', color: '#059669' },
    { name: 'Alex T.',   initials: 'AT', color: '#d97706' },
    { name: 'Dana P.',   initials: 'DP', color: '#db2777' },
  ]

  const ghosts = [
    { text: 'Q3 launch scope',      x: '4%',  delay: 0    },
    { text: 'Vendor SLA deadline',  x: '34%', delay: 0.77 },
    { text: 'Budget approval',      x: '63%', delay: 1.43 },
  ]

  const caught = [
    { label: 'Q3 launch scope',     owner: 'Jamie R.',  delay: 0    },
    { label: 'Vendor SLA deadline', owner: 'Sarah K.',  delay: 0.2  },
    { label: 'Budget approval',     owner: 'Marcus L.', delay: 0.37 },
  ]

  const ctrlBtns = [
    { icon: <Mic className="w-3.5 h-3.5" />,           label: 'Mute'         },
    { icon: <Video className="w-3.5 h-3.5" />,          label: 'Stop Video'   },
    { icon: <Users className="w-3.5 h-3.5" />,          label: 'Participants' },
    { icon: <MessageSquare className="w-3.5 h-3.5" />,  label: 'Chat'         },
    { icon: <MoreHorizontal className="w-3.5 h-3.5" />, label: 'More'         },
  ]

  return (
    <div className="relative w-full max-w-lg mx-auto select-none">
      <div className="absolute -inset-6 bg-gradient-to-br from-primary-500/10 via-accent-500/8 to-transparent rounded-3xl blur-2xl pointer-events-none" />
      <div className="absolute -bottom-4 left-1/4 right-1/4 h-16 bg-accent-500/15 rounded-full blur-2xl pointer-events-none" />

      <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/[0.07]">

        {/* ── Zoom header ──────────────────────────────────── */}
        <div className="flex items-center justify-between px-3 py-2 bg-[#131313] border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            {/* Zoom camera icon */}
            <div className="w-5 h-5 rounded-[3px] bg-[#1D6AE5] flex items-center justify-center flex-shrink-0">
              <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
                <path d="M0 1.4C0 .63.63 0 1.4 0H6.6C7.37 0 8 .63 8 1.4V3.8L11 2v4L8 4.2V6.6C8 7.37 7.37 8 6.6 8H1.4C.63 8 0 7.37 0 6.6V1.4Z" fill="white"/>
              </svg>
            </div>
            <span className="text-white/55 text-[11px] font-medium">Q3 Planning · All Hands</span>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="text-white/30 text-[11px] font-mono">42:18</span>
            <div className="flex items-center gap-1 bg-black/40 rounded px-1.5 py-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[9px] text-red-400 font-semibold tracking-wide">REC</span>
            </div>
          </div>
        </div>

        {/* ── Gallery grid ─────────────────────────────────── */}
        <div className="relative bg-[#1C1C1E]">

          {/* Ghost commitment labels — drift upward and off screen */}
          <div className="absolute inset-0 pointer-events-none z-10">
            {ghosts.map((g) => (
              <motion.div
                key={g.text}
                className="absolute text-[10px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap"
                style={{
                  left: g.x,
                  bottom: '30px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  color: 'rgba(255,255,255,0.5)',
                }}
                animate={
                  phase === 0
                    ? { y: [0, -30, -280, -400], opacity: [0, 0.85, 0.85, 0] }
                    : { y: 0, opacity: 0 }
                }
                transition={
                  phase === 0
                    ? { delay: g.delay, duration: 3.08, repeat: Infinity, ease: 'easeIn', times: [0, 0.06, 0.91, 1] }
                    : { duration: 0.2 }
                }
              >
                {g.text}
              </motion.div>
            ))}
          </div>

          {/* Participant tiles */}
          <div className="grid grid-cols-3 gap-px bg-black/50">
            {tiles.map((t, i) => (
              <div
                key={t.name}
                className="aspect-video relative flex items-center justify-center bg-[#2A2A2A] transition-shadow duration-300"
                style={{ boxShadow: speaker === i ? 'inset 0 0 0 2px #23D18B' : 'none' }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                  style={{ backgroundColor: t.color }}
                >
                  {t.initials}
                </div>
                {/* Zoom-style name badge — bottom left */}
                <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1">
                  <div className="w-3.5 h-3.5 rounded-sm bg-black/70 flex items-center justify-center flex-shrink-0">
                    <MicOff size={8} className="text-white/50" />
                  </div>
                  <span className="text-[8px] text-white/80 bg-black/60 px-1 py-px rounded-sm leading-tight">{t.name}</span>
                </div>
              </div>
            ))}

            {/* Rithmo tile */}
            <motion.div
              className="aspect-video relative flex items-center justify-center bg-[#0D1F1F]"
              animate={
                phase >= 1
                  ? { boxShadow: 'inset 0 0 0 2px rgba(22,193,178,0.8)' }
                  : { boxShadow: 'inset 0 0 0 2px transparent' }
              }
              transition={{ duration: 0.66 }}
            >
              <motion.div
                className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #0b6fa4 0%, #16c1b2 100%)' }}
                animate={phase === 1 ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                transition={phase === 1 ? { duration: 0.83, repeat: Infinity } : { duration: 0.33 }}
              >
                <img src="/rithmo-mark.png" alt="Rithmo" className="w-6 h-6 object-contain" />
              </motion.div>
              <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1">
                <div className="w-3.5 h-3.5 rounded-sm bg-black/70 flex items-center justify-center flex-shrink-0">
                  <Zap size={8} className="text-accent-400" />
                </div>
                <motion.span
                  className="text-[8px] font-semibold bg-black/60 px-1 py-px rounded-sm leading-tight"
                  animate={{ color: phase >= 1 ? '#16c1b2' : 'rgba(255,255,255,0.6)' }}
                  transition={{ duration: 0.44 }}
                >
                  Rithmo
                </motion.span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ── Rithmo status strip ──────────────────────────── */}
        <div className="px-3 py-2 bg-[#1C1C1E]">
          <motion.div
            className="rounded-lg px-3 py-1.5 flex items-center gap-2"
            initial={{ backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.06)' }}
            animate={
              phase >= 1
                ? { backgroundColor: 'rgba(22,193,178,0.08)', borderColor: 'rgba(22,193,178,0.25)' }
                : { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.06)' }
            }
            style={{ border: '1px solid' }}
            transition={{ duration: 0.55 }}
          >
            <motion.div
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              animate={
                phase >= 1
                  ? { backgroundColor: '#16c1b2', opacity: [1, 0.3, 1] }
                  : { backgroundColor: 'rgba(255,255,255,0.2)', opacity: 1 }
              }
              transition={phase >= 1 ? { duration: 1.21, repeat: Infinity } : { duration: 0.33 }}
            />
            <motion.span
              className="text-[10px] font-medium"
              animate={{ color: phase >= 1 ? '#16c1b2' : 'rgba(255,255,255,0.25)' }}
              transition={{ duration: 0.44 }}
            >
              {phase === 0
                ? 'Rithmo is observing this meeting'
                : phase === 1
                ? 'Catching commitments before they slip...'
                : 'All commitments secured.'}
            </motion.span>
          </motion.div>
        </div>

        {/* ── Zoom control bar ─────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-2.5 bg-[#131313] border-t border-white/[0.05]">
          <div className="flex items-center gap-4">
            {ctrlBtns.map((btn) => (
              <div key={btn.label} className="flex flex-col items-center gap-0.5">
                <div className="w-8 h-7 rounded bg-[#383838] flex items-center justify-center text-white/65">
                  {btn.icon}
                </div>
                <span className="text-[7px] text-white/30 whitespace-nowrap">{btn.label}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <div className="flex items-center gap-1.5 bg-[#E53E3E] rounded px-3 h-7">
              <PhoneOff className="w-3 h-3 text-white" />
              <span className="text-[9px] text-white font-semibold">End</span>
            </div>
            <span className="text-[7px] text-white/30"> </span>
          </div>
        </div>

        {/* ── Caught commitments ───────────────────────────── */}
        <div className="bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] text-gray-400 font-mono uppercase tracking-widest">
              {phase < 2 ? 'Catching...' : 'Caught and Confirmed'}
            </p>
            <AnimatePresence>
              {phase === 2 && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-[10px] text-success font-semibold"
                >
                  0 open items
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          <div className="space-y-2.5">
            {caught.map((item) => (
              <motion.div
                key={item.label}
                animate={phase >= 1 ? { opacity: 1, x: 0 } : { opacity: 0.12, x: -6 }}
                transition={{ delay: phase >= 1 ? item.delay : 0, duration: 0.39 }}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-success flex-shrink-0" />
                  <span className="text-xs text-gray-700">{item.label}</span>
                </div>
                <motion.span
                  animate={phase >= 1 ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ delay: phase >= 1 ? item.delay + 0.15 : 0, duration: 0.33 }}
                  className="text-[10px] text-success font-semibold whitespace-nowrap"
                >
                  {item.owner}
                </motion.span>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </div>
  )
}

// ─── Org Health Table ─────────────────────────────────────────────────────────

function OrgHealthTable() {
  const teams = [
    { name: 'Leadership', score: 91, trend: [78, 82, 85, 88, 90, 91], status: 'strong',    change: '+13', down: false },
    { name: 'Product',    score: 82, trend: [85, 84, 83, 81, 82, 82], status: 'stable',    change: '-3',  down: false },
    { name: 'Engineering',score: 74, trend: [70, 72, 73, 74, 74, 74], status: 'improving', change: '+4',  down: false },
    { name: 'Revenue',    score: 55, trend: [68, 64, 60, 58, 56, 55], status: 'declining', change: '-13', down: true  },
    { name: 'Operations', score: 38, trend: [62, 55, 50, 44, 42, 38], status: 'at risk',   change: '-24', down: true  },
  ]

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <p className="text-xs text-gray-400 font-mono uppercase tracking-widest">Organization Health · 6-Week View</p>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
          <span className="text-xs text-gray-400">live</span>
        </div>
      </div>

      {/* Column headers */}
      <div className="flex items-center gap-4 px-6 py-2 bg-gray-50 border-b border-gray-100">
        <span className="text-[10px] text-gray-400 uppercase tracking-wider w-28 flex-shrink-0">Team</span>
        <span className="text-[10px] text-gray-400 uppercase tracking-wider flex-1">Health</span>
        <span className="text-[10px] text-gray-400 uppercase tracking-wider w-8 text-right flex-shrink-0">Score</span>
        <span className="text-[10px] text-gray-400 uppercase tracking-wider w-20 text-center flex-shrink-0">6-Week Trend</span>
        <span className="text-[10px] text-gray-400 uppercase tracking-wider w-8 text-right flex-shrink-0">Change</span>
        <span className="text-[10px] text-gray-400 uppercase tracking-wider w-20 text-center flex-shrink-0">Status</span>
      </div>

      <div className="divide-y divide-gray-50">
        {teams.map((team, i) => (
          <motion.div
            key={team.name}
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50/50 transition-colors"
          >
            <span className="text-sm font-semibold text-gray-800 w-28 flex-shrink-0">{team.name}</span>

            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${team.score}%` }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.08, duration: 0.7 }}
                className={`h-full rounded-full ${
                  team.score < 50 ? 'bg-error' : team.score < 65 ? 'bg-warning' : 'bg-gradient-primary'
                }`}
              />
            </div>

            <span className={`text-sm font-bold w-8 text-right flex-shrink-0 tabular-nums ${
              team.score < 50 ? 'text-error' : team.score < 65 ? 'text-warning' : 'text-gray-900'
            }`}>{team.score}</span>

            <div className="w-20 flex justify-center flex-shrink-0">
              <SparkLine data={team.trend} down={team.down} />
            </div>

            <span className={`text-xs font-semibold w-8 text-right flex-shrink-0 tabular-nums ${team.down ? 'text-error' : 'text-success'}`}>
              {team.change}
            </span>

            <div className="w-20 flex justify-center flex-shrink-0">
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
                team.status === 'at risk'   ? 'bg-error/10 text-error' :
                team.status === 'declining' ? 'bg-warning/10 text-warning' :
                team.status === 'improving' ? 'bg-success/10 text-success' :
                                              'bg-gray-100 text-gray-500'
              }`}>{team.status}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

// ─── Multi-Line Trend Chart ───────────────────────────────────────────────────

function TrendChart() {
  const weeks = ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8']
  const lines = [
    { label: 'Alignment',   data: [88, 86, 85, 82, 80, 78, 76, 74], color: '#0b6fa4', dashed: false },
    { label: 'Consistency', data: [72, 74, 75, 77, 79, 80, 82, 84], color: '#16c1b2', dashed: false },
    { label: 'Friction',    data: [22, 25, 28, 32, 36, 40, 44, 48], color: '#ef4444', dashed: true  },
  ]

  const W = 400, H = 120
  const allVals = lines.flatMap(l => l.data)
  const max = Math.max(...allVals) + 8
  const min = Math.min(...allVals) - 8

  const getD = (data) => data.map((v, i) => {
    const x = (i / (data.length - 1)) * W
    const y = H - ((v - min) / (max - min)) * H
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
  }).join(' ')

  return (
    <div>
      <div className="flex items-center gap-6 mb-6">
        {lines.map(l => (
          <div key={l.label} className="flex items-center gap-2">
            <svg width="20" height="12" viewBox="0 0 20 12">
              <line x1="0" y1="6" x2="20" y2="6" stroke={l.color} strokeWidth="2" strokeDasharray={l.dashed ? '4 2' : undefined} strokeLinecap="round" />
            </svg>
            <span className="text-xs text-dark-300">{l.label}</span>
          </div>
        ))}
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H + 24}`} className="overflow-visible">
        {[25, 50, 75].map(pct => {
          const y = H - ((pct - min) / (max - min)) * H
          return <line key={pct} x1="0" y1={y} x2={W} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        })}
        {weeks.map((wk, i) => (
          <text key={wk} x={(i / (weeks.length - 1)) * W} y={H + 18} textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.3)">{wk}</text>
        ))}
        {lines.map(line => (
          <motion.path
            key={line.label}
            d={getD(line.data)}
            fill="none"
            stroke={line.color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={line.dashed ? '5 3' : undefined}
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: 'easeInOut' }}
          />
        ))}
      </svg>
    </div>
  )
}

// ─── FAQ Item ─────────────────────────────────────────────────────────────────

// ─── Live Leadership Card ─────────────────────────────────────────────────────

const wait = (ms) => new Promise((res) => setTimeout(res, ms))

function LiveLeadershipCard() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.5 })
  const [phase, setPhase] = useState(0)

  // phase 0: meeting wrapping up, 3 action items with no owners
  // phase 1: Rithmo intervenes, captures ownership in real time
  // phase 2: all 3 items have assigned owners
  useEffect(() => {
    if (!isInView) return
    let cancelled = false
    const run = async () => {
      while (!cancelled) {
        setPhase(0)
        await wait(4000)
        if (cancelled) break
        setPhase(1)
        await wait(3500)
        if (cancelled) break
        setPhase(2)
        await wait(4500)
      }
    }
    run()
    return () => { cancelled = true }
  }, [isInView])

  const items = [
    { task: 'Align Q3 capacity plan',  owner: 'Jamie R.' },
    { task: 'Finalize vendor SLAs',    owner: 'Sarah K.' },
    { task: 'Update team OKRs',        owner: 'Marcus L.' },
  ]

  return (
    <div ref={ref} className="rounded-2xl border border-accent-500/20 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-dark-600/90">
        <div className="flex items-center gap-2">
          <motion.div
            className="w-2 h-2 rounded-full bg-accent-400"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
          <span className="text-xs font-semibold text-white">Live · Operations Weekly Sync</span>
        </div>
        <span className="text-[10px] text-dark-200 font-mono">Rithmo Active</span>
      </div>

      <div className="bg-gradient-to-br from-primary-50/60 to-accent-50/40 p-5 space-y-4">

        {/* Action items — fixed height, two states overlaid */}
        <div>
          <p className="text-[10px] text-gray-400 font-mono uppercase tracking-wider mb-2">Action Items · End of Meeting</p>
          <div className="relative h-[90px]">

            {/* State A: unassigned */}
            <motion.div
              animate={{ opacity: phase < 2 ? 1 : 0 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 space-y-2"
            >
              {items.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-xs text-gray-700 truncate pr-4">{item.task}</span>
                  <span className="text-xs font-semibold text-error flex-shrink-0">Unassigned</span>
                </div>
              ))}
            </motion.div>

            {/* State B: owners assigned */}
            <motion.div
              animate={{ opacity: phase >= 2 ? 1 : 0 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 space-y-2"
            >
              {items.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-xs text-gray-700 truncate pr-4">{item.task}</span>
                  <span className="text-xs font-semibold text-success flex-shrink-0">{item.owner}</span>
                </div>
              ))}
            </motion.div>

          </div>
        </div>

        {/* Rithmo signal — always in DOM, opacity only */}
        <motion.div
          animate={{ opacity: phase >= 1 ? 1 : 0 }}
          transition={{ duration: 0.4 }}
          className="bg-dark-700 rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-3.5 h-3.5 text-accent-400 flex-shrink-0" />
            <span className="text-[10px] font-semibold text-accent-400 uppercase tracking-wider">Rithmo · Now</span>
          </div>
          <div className="relative h-[40px]">
            <motion.p
              animate={{ opacity: phase === 1 ? 1 : 0 }}
              transition={{ duration: 0.35 }}
              className="absolute inset-0 text-sm text-white leading-snug"
            >
              3 action items leaving without owners. Capturing ownership before the meeting closes.
            </motion.p>
            <motion.p
              animate={{ opacity: phase === 2 ? 1 : 0 }}
              transition={{ duration: 0.35 }}
              className="absolute inset-0 text-sm text-success leading-snug"
            >
              Ownership captured for all 3 items. Accountability confirmed.
            </motion.p>
          </div>
        </motion.div>

      </div>
    </div>
  )
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-gray-100">
      <button
        className="w-full flex items-center justify-between py-5 text-left gap-4"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="font-semibold text-gray-900">{question}</span>
        <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-gray-600 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const painPoints = [
  { icon: <Building2 className="w-5 h-5" />, title: 'There Is Always More Happening Than Any Leader Can Track', desc: 'Decisions, commitments, and risks are forming in meetings every day. Most of them never surface to you until they are already expensive to address.' },
  { icon: <BarChart3 className="w-5 h-5" />, title: 'What Gets Agreed Does Not Always Turn Into Action', desc: 'The gap between what gets decided and what actually gets done is where most organizations quietly lose ground. Commitments get made without owners. Follow through gets assumed rather than confirmed.' },
  { icon: <Activity className="w-5 h-5" />, title: 'Small Gaps Compound Into Big Problems Before Anyone Notices', desc: 'A missed follow through here, an unresolved question there. On their own they are small. Together, over weeks, they become the delays and disconnects that take months to untangle.' },
  { icon: <Eye className="w-5 h-5" />, title: 'Until Now, There Was No Good Way to Stay Connected to All of It', desc: 'No tool existed that could attend every meeting, remember every commitment, flag every risk, and brief a leader in seconds. That is what Rithmo is built to do.' },
]

const workflowSteps = [
  { icon: <Users className="w-5 h-5" />,    label: 'Meetings Continue as Normal',           desc: 'Zero disruption to your team or their workflows.' },
  { icon: <Eye className="w-5 h-5" />,      label: 'Rithmo Attends and Pays Attention',     desc: 'Every decision, commitment, and open question captured.' },
  { icon: <Brain className="w-5 h-5" />,    label: 'Patterns and Risks Surface Early',      desc: 'What needs attention finds its way to you before it becomes a problem.' },
  { icon: <BarChart3 className="w-5 h-5" />,label: 'Leaders Stay Informed Effortlessly',    desc: 'One clear view of how every team and engagement is tracking.' },
  { icon: <Zap className="w-5 h-5" />,      label: 'Your Team Executes at Their Best',      desc: 'Nothing slips. Nothing is forgotten. Everyone stays on track.' },
]

const exampleInsights = [
  { text: 'Planning meetings across Product and Engineering showed declining commitment follow through over 3 consecutive weeks. Rithmo flagged it before it impacted the roadmap.', teams: ['Product', 'Engineering'], severity: 'warn', ago: '2 days ago' },
  { text: 'Repeated operational discussions occurred across three consecutive meetings without clear ownership resolution.', teams: ['Operations'], severity: 'alert', ago: '3 days ago' },
  { text: '4 strategic decisions from Q3 planning were still unresolved heading into the new quarter. Rithmo surfaced them before they impacted the roadmap.', teams: ['Leadership'], severity: 'warn', ago: '4 days ago' },
  { text: 'Multiple strategic initiatives lacked clearly assigned accountability across Q2 planning sessions.', teams: ['Strategy', 'Revenue'], severity: 'alert', ago: '5 days ago' },
]

const faqs = [
  { question: 'Does Rithmo replace our current meeting platform?', answer: 'No. Rithmo works alongside your existing meeting setup. Compatible with Zoom and Microsoft Teams.' },
  { question: 'Does Rithmo actively participate in meetings?', answer: 'No. Organizations can choose whether Rithmo actively participates or simply observes silently. Most leadership teams begin in silent observation mode.' },
  { question: 'Can leadership see individual employee analytics?', answer: 'No. Personal analytics including contribution scores, participation data, and action items are visible only to the individual. Leadership and org admins see team and org-level aggregates only. This boundary is enforced at the data level, not through a permission setting that can be toggled. Employees can trust that their personal data stays personal.' },
  { question: 'Is Rithmo a monitoring platform?', answer: 'No. Rithmo is designed to help organizations improve communication visibility, operational consistency, and meeting quality. It is not designed for employee monitoring or surveillance.' },
  { question: 'Is Rithmo difficult to deploy or manage?', answer: 'No. Setup takes under a minute. No training, no dedicated administrators, no ongoing configuration.' },
  { question: 'Who is Rithmo designed for?', answer: 'Any leader who wants to ensure their team is executing at their best and that nothing important falls through. That includes managers, directors, VPs, and C-suite leaders. Rithmo works across internal teams and external engagements with vendors, agencies, and partners.' },
]

// ─── Org Score Card ───────────────────────────────────────────────────────────

const orgScoreInsights = [
  {
    title: 'Decisions closing faster',
    body: 'Teams are resolving open items in fewer meetings. Less carrying over week to week.',
  },
  {
    title: 'Unresolved commitments down from 12 to 3',
    body: 'Rithmo confirmed ownership on 9 items that previously had no clear next step.',
  },
  {
    title: 'Two teams moved from at risk to on track',
    body: 'Consistent follow through this cycle moved both Operations and Revenue in the right direction.',
  },
]

function OrgScoreCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-md p-6 md:p-8">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0 pr-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <h3 className="font-bold text-gray-900 text-base">Org Score</h3>
            <Info className="w-4 h-4 text-gray-400 flex-shrink-0" />
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            Your org score is up 11 points this cycle. Here is what Rithmo changed.
          </p>
          <p className="text-xs text-gray-400 mt-2">Last updated May 18, 2026, 12:51 PM</p>
        </div>
        <motion.div
          className="flex-shrink-0 bg-success/5 border border-success/15 rounded-xl px-5 py-3 text-center"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.4, ease: 'easeOut' }}
        >
          <div className="flex items-center justify-center gap-2 mb-0.5">
            <p className="text-lg font-semibold text-gray-300">68</p>
            <p className="text-xs text-gray-300">→</p>
            <p className="text-4xl font-bold text-success">79</p>
          </div>
          <p className="text-[10px] text-success font-semibold">↑ +11 this cycle</p>
        </motion.div>
      </div>

      <div className="border-t border-gray-100 my-5" />

      <div className="space-y-5">
        {orgScoreInsights.map((insight, i) => (
          <motion.div
            key={insight.title}
            className="flex gap-3"
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 + i * 0.12, duration: 0.4 }}
          >
            <div className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-1.5" />
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-1">{insight.title}</p>
              <p className="text-xs text-gray-500 leading-relaxed">{insight.body}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

// ─── This Is Rithmo — Sequential Video Tiles ─────────────────────────────────

const TILES = [
  { num: 'Meeting 1', title: 'Captures the Commitment',    desc: 'Rithmo logs what was agreed and confirms who owns it, right as it happens.',                    src: '/videos/rithmo-tile-1.mp4' },
  { num: 'Meeting 2', title: 'Remembers Across Meetings',  desc: 'If a commitment is still open heading into the next meeting, Rithmo brings it back.',            src: '/videos/rithmo-tile-2.mp4' },
  { num: 'Meeting 3', title: 'Flags the Risk Early',       desc: 'Rithmo surfaces high risk items before they turn into delays.',                                  src: '/videos/rithmo-tile-3.mp4' },
  { num: 'Meeting 4', title: 'Everyone Wins',              desc: 'The team hits its deadline. Nothing slipped through.',                                           src: '/videos/rithmo-tile-4.mp4' },
]

function RithmoTiles() {
  const [active, setActive] = useState(null)
  const [done, setDone] = useState(new Set())
  const refs = useRef([null, null, null, null])

  const started = active !== null || done.size > 0

  const startSequence = () => {
    refs.current.forEach(v => { if (v) v.currentTime = 0 })
    setDone(new Set())
    setActive(0)
    refs.current[0]?.play()
  }

  const handleEnded = (i) => {
    setDone(prev => new Set([...prev, i]))
    if (i < TILES.length - 1) {
      setActive(i + 1)
      refs.current[i + 1]?.play()
    } else {
      setActive(null)
    }
  }

  return (
    <div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {TILES.map((tile, i) => {
          const isPlaying = active === i
          const isDone = done.has(i)
          const isWaiting = !isPlaying && !isDone

          return (
            <motion.div
              key={tile.num}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i + 1) * 0.1 }}
              className={`rounded-2xl border overflow-hidden transition-all duration-500 ${isPlaying ? 'border-accent-400 shadow-glow-sm' : 'border-gray-100'}`}
            >
              <div className="relative aspect-square bg-gray-100">
                <video
                  ref={el => { refs.current[i] = el }}
                  src={tile.src}
                  className={`w-full h-full object-cover object-center transition-all duration-700 ${isWaiting && i !== 0 ? 'grayscale' : 'grayscale-0'}`}
                  onEnded={() => handleEnded(i)}
                  playsInline
                />

                {/* Gray overlay on waiting tiles — skip tile 1 so play button stands out */}
                {isWaiting && i !== 0 && (
                  <div className="absolute inset-0 bg-white/30" />
                )}

                {/* Play / replay button — visible whenever nothing is playing */}
                {i === 0 && active === null && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      onClick={startSequence}
                      className="w-14 h-14 rounded-full bg-white/95 shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
                    >
                      <Play className="w-6 h-6 text-gray-900 ml-1" />
                    </button>
                  </div>
                )}

                {/* Playing pill */}
                {isPlaying && (
                  <div className="absolute bottom-2 left-2 flex items-center gap-1.5 bg-black/55 backdrop-blur-sm rounded-full px-2.5 py-1">
                    <motion.div
                      className="w-1.5 h-1.5 rounded-full bg-accent-400"
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ duration: 0.9, repeat: Infinity }}
                    />
                    <span className="text-[9px] text-white font-medium">Playing</span>
                  </div>
                )}
              </div>

              <div className="p-5 bg-white">
                <p className={`text-xs font-bold mb-2 uppercase tracking-widest transition-colors duration-500 ${isWaiting && started ? 'text-gray-300' : 'text-accent-600'}`}>{tile.num}</p>
                <h3 className={`font-bold mb-1 transition-colors duration-500 ${isWaiting && started ? 'text-gray-300' : 'text-gray-900'}`}>{tile.title}</h3>
                <p className={`text-sm leading-relaxed transition-colors duration-500 ${isWaiting && started ? 'text-gray-200' : 'text-gray-500'}`}>{tile.desc}</p>
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* Step progress dots */}
      <div className="flex items-center justify-center gap-3 mt-8">
        {TILES.map((_, i) => (
          <div
            key={i}
            className={`rounded-full transition-all duration-500 ${
              active === i ? 'w-6 h-2 bg-accent-500' :
              done.has(i) ? 'w-2 h-2 bg-accent-300' :
              'w-2 h-2 bg-gray-200'
            }`}
          />
        ))}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function LeadershipPage() {
  usePageMeta({
    title: 'Rithmo for Leaders at Every Level: Nothing Slips, Nothing Gets Missed',
    description: 'Rithmo keeps track of every decision, commitment, and open risk across your organization so you can lead with confidence and your team can execute at their best.',
  })
  return (
    <div className="min-h-screen bg-white">
      <Navbar transparent />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/40 via-white to-white" />
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-primary-500/8 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-accent-500/8 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-100 text-primary-700 text-xs font-semibold mb-6"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
                Organizational Intelligence
              </motion.div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1] mb-6">
                Lead With the Confidence That{' '}
                <span className="gradient-text">Nothing Important Is Slipping Through.</span>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed mb-10 max-w-xl">
                Rithmo keeps track of every decision, every commitment, and every open question across your organization, so you can focus on leading and your team can stay focused on executing at their best.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <Link to="/signup" className="btn-primary text-lg px-8 py-4">Get Early Access</Link>
              </div>

            </motion.div>

            <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
              <HeroVisualization />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── This Is Rithmo ───────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14 max-w-2xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              This is <span className="gradient-text">Rithmo.</span>
            </h2>
            <p className="text-lg text-gray-600">
              A photorealistic AI participant who joins your Zoom calls just like a human attendee. She listens to every word, speaks when a commitment needs an owner or a risk needs flagging, and never forgets a detail. Not a recording tool. Not a bot icon in the corner. A real presence in the room your team will actually respond to.
            </p>
          </motion.div>

          <RithmoTiles />
        </div>
      </section>

      {/* ── Pain Points ────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-white" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight">
                The hardest part of leadership is not the strategy,{' '}
                <span className="gradient-text">it's the execution follow through.</span>
              </h2>
            </motion.div>
            <div className="space-y-6 pt-2">
              {painPoints.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center text-white flex-shrink-0 shadow-glow-sm">
                    {p.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-0.5">{p.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{p.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Autonomous Action ─────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left: copy */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Rithmo Catches It Early.{' '}
                <span className="gradient-text">You Decide.</span>{' '}
                Rithmo Makes Sure It Gets Done.
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Rithmo spots the things that need attention before they become problems. One decision from you and Rithmo takes care of the rest, ensuring nothing drifts and your team stays on track.
              </p>
            </motion.div>

            {/* Right: signal → approve → action flow */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="space-y-3">

              {/* Step 1: Signal */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl border border-accent-500/20 shadow-sm p-5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
                  <span className="text-xs font-semibold text-accent-600 uppercase tracking-widest">Rithmo Insight</span>
                </div>
                <p className="text-sm text-gray-800 leading-relaxed mb-4">
                  Q3 planning sessions have produced 4 commitments without confirmed owners across 3 consecutive meetings. Confirming ownership now keeps execution on track.
                </p>
                <div className="flex items-center gap-3">
                  <button className="px-4 py-2 rounded-lg bg-gradient-primary text-white text-xs font-semibold shadow-glow-sm pointer-events-none">
                    Confirm and Continue
                  </button>
                  <button className="px-4 py-2 rounded-lg border border-gray-200 text-gray-500 text-xs font-medium pointer-events-none">
                    Dismiss
                  </button>
                </div>
              </motion.div>

              {/* Connector */}
              <div className="flex justify-center">
                <div className="w-px h-5 bg-gray-200" />
              </div>

              {/* Step 2: Approved */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-success/5 rounded-2xl border border-success/20 p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center flex-shrink-0">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <span className="text-sm font-semibold text-gray-800">Leadership Approved</span>
                  </div>
                  <span className="text-xs text-gray-400">2 minutes ago</span>
                </div>
              </motion.div>

              {/* Connector */}
              <div className="flex justify-center">
                <div className="w-px h-5 bg-gray-200" />
              </div>

              {/* Step 3: Animated live signal */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
              >
                <LiveLeadershipCard />
              </motion.div>

            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Org Health Dashboard ──────────────────────────────────────────── */}
      <section className="pt-12 pb-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 mb-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight">
                Every Team's Health.{' '}
                <span className="gradient-text">One View.</span>
              </h2>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <p className="text-lg text-gray-600 leading-relaxed">
                Rithmo tracks how every team is executing, what is getting resolved, and where things need attention, giving leaders a clear picture without requiring anyone to sit in every meeting.
              </p>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}>
            <OrgHealthTable />
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 mt-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight">
                One Number for the Whole Organization.
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                The Org Score rolls up meeting quality across every team into a single number. Rithmo shows you what is driving it, which teams are strong, and where to focus.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <OrgScoreCard />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── What Rithmo Surfaces ──────────────────────────────────────────── */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute -top-20 right-0 w-96 h-96 bg-accent-500/8 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-500/8 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left: copy */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Like Having a Chief of Staff{' '}
                <span className="gradient-text">Across Every Meeting</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                A great Chief of Staff attends the meetings you cannot, remembers everything that matters, and makes sure nothing falls through. Rithmo does the same, continuously, across every team and every engagement your organization runs.
              </p>
            </motion.div>

            {/* Right: executive briefing card */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}>
              <div className="bg-white rounded-2xl border border-gray-150 shadow-md overflow-hidden">

                {/* Briefing header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                  <div>
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-widest">Weekly Executive Briefing</p>
                    <p className="text-xs text-gray-400 mt-0.5">Prepared by Rithmo · Week of May 26</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                    <span className="text-xs text-gray-400">live</span>
                  </div>
                </div>

                {/* Team status */}
                <div className="px-6 pt-5 pb-3">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Team Status</p>
                  <div className="space-y-1">
                    {[
                      { team: 'Leadership',   dot: 'bg-success',  status: 'Strong',    note: 'Decisions clear, commitments confirmed' },
                      { team: 'Product',      dot: 'bg-success',  status: 'On Track',  note: 'Q3 priorities aligned and moving' },
                      { team: 'Engineering',  dot: 'bg-accent-500', status: 'Improving', note: 'Decision velocity up 3 points this cycle' },
                      { team: 'Revenue',      dot: 'bg-warning',  status: 'Watch',     note: '3 open decisions need resolution this week' },
                      { team: 'Operations',   dot: 'bg-warning',  status: 'Needs Help', note: '4 commitments unconfirmed from last week' },
                    ].map((row, i) => (
                      <motion.div
                        key={row.team}
                        initial={{ opacity: 0, x: 8 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + i * 0.07 }}
                        className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0"
                      >
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${row.dot}`} />
                        <span className="text-sm font-semibold text-gray-800 w-24 flex-shrink-0">{row.team}</span>
                        <span className={`text-xs font-medium w-16 flex-shrink-0 ${
                          row.status === 'At Risk' ? 'text-error' :
                          row.status === 'Friction' ? 'text-warning' :
                          row.status === 'Improving' ? 'text-accent-600' : 'text-success'
                        }`}>{row.status}</span>
                        <span className="text-xs text-gray-400 leading-tight">{row.note}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Flags */}
                <div className="px-6 pt-3 pb-5 bg-gray-50 border-t border-gray-100">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Flags for Your Attention</p>
                  <div className="space-y-2.5">
                    {[
                      '3 strategic decisions from last week remain unresolved and need owners before Thursday.',
                      '4 commitments in Operations have not been confirmed. Rithmo can follow up.',
                      'Revenue and Engineering are misaligned on Q3 scope. A 30-minute sync would close it.',
                    ].map((flag, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.7 + i * 0.1 }}
                        className="flex items-start gap-2.5"
                      >
                        <ArrowRight className="w-3.5 h-3.5 text-accent-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-600">{flag}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── Trends — dark ─────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ background: 'radial-gradient(ellipse at 70% 50%, rgba(22,193,178,0.15) 0%, transparent 60%)' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Rithmo Understands More Than{' '}
                <span className="gradient-text">Meeting Activity</span>
              </h2>
              <p className="text-lg text-dark-100 leading-relaxed mb-6">
                Most meeting tools record what happened. Rithmo understands what it means, tracking how your teams are trending across the dimensions that determine whether your organization is building momentum or quietly losing it.
              </p>
              <div className="space-y-3">
                {['Alignment', 'Accountability', 'Communication Quality', 'Decision Clarity', 'Commitment Follow Through', 'Team Momentum'].map((label, i) => (
                  <motion.div key={label} initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0" />
                    <span className="text-dark-200 text-sm">{label}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <p className="text-xs text-dark-300 font-mono uppercase tracking-widest mb-6">Organizational Trends · 8 Weeks</p>
              <TrendChart />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Workflow ───────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-50" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Simple Enough to Fit Into{' '}
              <span className="gradient-text">Existing Leadership Workflows</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-4">
            {workflowSteps.map((step, i) => (
              <div key={step.label} className="flex md:flex-col items-start md:items-center gap-4 md:gap-0">
                <div className="flex md:flex-col items-center md:w-full">
                  <motion.div initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }} className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center flex-shrink-0 text-white shadow-glow-sm">
                    {step.icon}
                  </motion.div>
                  {i < workflowSteps.length - 1 ? (
                    <div className="hidden md:flex items-center justify-center w-full my-3">
                      <ArrowRight className="w-4 h-4 text-gray-300" />
                    </div>
                  ) : (
                    <div className="hidden md:block w-full my-3 h-4" />
                  )}
                </div>
                <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 + 0.08 }} className="md:text-center md:mt-3">
                  <p className="font-semibold text-gray-900 text-sm mb-1">{step.label}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                </motion.div>
              </div>
            ))}
          </div>

          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.7 }} className="text-center text-gray-500 mt-14 text-lg">
            Rithmo gives every leader the peace of mind that their team is on track and nothing important is being missed.
          </motion.p>
        </div>
      </section>

      {/* ── Security ─────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary-500/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 mb-16 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-600 mb-4">Security and Trust</span>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight">
                Built to Earn Trust,{' '}
                <span className="gradient-text">Not Trigger Concern</span>
              </h2>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <p className="text-lg text-gray-600 leading-relaxed">
                Leadership teams often face internal questions before rolling out any new organizational tool. Rithmo is designed to make those conversations easy.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Not Employee Surveillance',
                body: 'Rithmo surfaces organizational patterns, not individual behavior. It is designed to improve meeting quality across teams, not monitor or evaluate employees.',
              },
              {
                title: 'Data Stays in Your Organization',
                body: 'Meeting content belongs to your organization. Rithmo does not sell, share, or use your data outside of delivering intelligence back to your leadership team.',
              },
              {
                title: 'Disconnect at Any Time',
                body: 'Rithmo connects via a simple OAuth integration. Any administrator can revoke access immediately without contacting support or waiting on a vendor.',
              },
              {
                title: 'No Recording Retention',
                body: 'Rithmo analyzes meetings for organizational signals. Raw audio is never stored. Nothing is retained that could create a compliance or privacy liability.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-accent-500/30 hover:bg-white hover:shadow-md transition-all duration-300"
              >
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Future Vision ─────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse at 60% 50%, rgba(22,193,178,0.15) 0%, transparent 60%)' }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mx-auto mb-6 shadow-glow">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Built For the Future of{' '}
              <span className="gradient-text">AI-Native Organizations</span>
            </h2>
            <p className="text-lg text-dark-100 leading-relaxed">
              The future of organizational intelligence is not another recording tool. It is a layer that understands how teams are executing, what is getting resolved, and what needs a leader's attention, surfacing the right signals at the right time so every team can operate at their best. Rithmo is built for that future.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(22,193,178,0.12) 0%, transparent 60%)' }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Know That Everything Important Is Covered.{' '}
              <span className="gradient-text">Every Single Day.</span>
            </h2>
            <p className="text-lg text-dark-100 mb-10 leading-relaxed">
              Rithmo is working with early organizations who want to lead with confidence, keep their teams executing at their best, and never be caught off guard by something that was visible weeks ago.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="btn-primary text-lg px-8 py-4">Get Early Access</Link>
              <Link to="/contact" className="btn-secondary text-lg px-8 py-4">Talk to the Team</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-2xl md:text-3xl font-bold text-gray-900 mb-12 text-center">
            Common Questions
          </motion.h2>
          <div>
            {faqs.map(faq => <FaqItem key={faq.question} question={faq.question} answer={faq.answer} />)}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default LeadershipPage
